import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Product from './Product';
import FormInput from '../../common/FormInput';
import { PageNames, ProductTypes, RoutePaths } from '../../../Constants/Common';
import Selectable from '../../common/Selectable';
import { PolicyApi } from '../../../api/PolicyApi';
import LoadingContainer from '../../common/loadingContainer/LoadingContainer';
import { SiteContext } from '../../../context/SiteContext';
import { GoogleAnalytics } from '../../../classes/GoogleAnalytics';
import ModalDialog from '../../common/ModalDialog';
import './ProductPage.scss';
import ProgressButton from '../../common/ProgressButton';
import ReloadHandler from '../../common/ReloadHandler';
import { useSelectedVehicle } from '../../hooks/useSelectedVehicle';
import { useAuth0 } from '@auth0/auth0-react';
import { ProductType } from '../../common/CommonTypes';
import { t } from 'i18next';
const ProductPage = (): JSX.Element => {
    const { getAccessTokenSilently, user } = useAuth0();
    const { brandId } = useContext(SiteContext);
    const vehicle = useSelectedVehicle();
    const [products, setProducts] = useState<ProductType[]>([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);

    const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
        undefined
    );

    const navigate = useNavigate();

    useEffect(() => {
        if (brandId !== 0 && vehicle !== undefined) {
            loadPolicyDetails();
        }
        GoogleAnalytics.setPageView(PageNames.Product);
    }, []);

    const loadPolicyDetails = async () => {
        const successCallback = (policies: ProductType[]) => {
            setProducts(policies);
            setLoading(false);
        };

        const failureCallback = () => {
            setLoading(false);
        };

        const token = await getAccessTokenSilently();

        new PolicyApi().getPolicyDetails(
            vehicle.vrn,
            vehicle.dateOfFirstRegistration,
            brandId,
            user.email,
            token,
            successCallback,
            failureCallback
        );
    };

    const onProductClicked = useCallback((selected: boolean, index: number) => {
        if (selected) {
            setSelectedIndex(index);
        } else {
            setSelectedIndex(undefined);
        }
    }, []);

    const onRequestRepairClicked = (event: React.FormEvent) => {
        if (selectedIndex === undefined) {
            event.preventDefault();
        } else {
            if (
                products[selectedIndex].type === ProductTypes.JointAlloyCosmetic
            ) {
                event.preventDefault();
                setShowDialog(true);
            }
        }
    };

    const onPriorRepairRequestClicked = (event: React.FormEvent) => {
        if (selectedIndex === undefined) {
            event.preventDefault();
        }
    };

    const onCloseModalClick = () => {
        return setShowDialog(false);
    };

    const onJointAlloyConfirmation = () => {
        handleJointAlloyCosmeticNavigation(RoutePaths.Alloy);
    };

    const onJointCosmeticConfirmation = () => {
        handleJointAlloyCosmeticNavigation(RoutePaths.Cosmetic);
    };

    const handleJointAlloyCosmeticNavigation = (routePath: string) => {
        const locationState = {
            product: products[selectedIndex]
        };

        navigate(routePath, { state: locationState });
    };

    const sharedrequestButtonStyle = 'btn btn-primary progress-button';
    const requestButtonStyle =
        selectedIndex === undefined
            ? `${sharedrequestButtonStyle} disabled`
            : `${sharedrequestButtonStyle}`;

    const getProductRoute = (selectedProduct) => {
        switch (selectedProduct?.type) {
            case ProductTypes.Alloy:
            case ProductTypes.AlloyRepair:
                return RoutePaths.Alloy;
            case ProductTypes.Cosmetic:
            case ProductTypes.CosmeticRepair:
                return RoutePaths.Cosmetic;
            case ProductTypes.JointAlloyCosmetic:
                return RoutePaths.ToResolveFromProductType;
            default:
                return undefined;
        }
    };

    const createProductNavigation = () => {
        const selectedProduct =
            selectedIndex === undefined ? undefined : products[selectedIndex];

        if (
            selectedIndex !== undefined &&
            getProductRoute(selectedProduct) === undefined
        ) {
            return (
                <p className='error'>
                    {t('cannot_request_a_repair_for_this_product')}
                </p>
            );
        }

        const productRoute = getProductRoute(selectedProduct) || '/';

        return (
            <ProgressButton
                onClick={onRequestRepairClicked}
                navRoute={productRoute}
                locationState={{ product: selectedProduct }}
                buttonStyle={requestButtonStyle}
                text='Request a Repair'
            />
        );
    };

    const navButton = createProductNavigation();
    return (
        <>
            <LoadingContainer loading={loading} message={t('loading')}>
                <>
                    <div className='card page-content'>
                        <div className='card-header page-content-header'>
                            <div className='page-content-header-container'>
                                <h4>{t('products_title')}</h4>
                            </div>
                        </div>
                        <div className='card-header'>
                            <p className='card-title'>{vehicle?.vrn}</p>
                            <span className='text-muted'>{`${vehicle?.make} ${vehicle?.model}`}</span>
                        </div>
                        <div className='card-body page-content-body'>
                            <FormInput
                                id='product-forminput'
                                label='Select your product'>
                                {products.map((product, index) => (
                                    <Selectable
                                        id={`selectable-product-${index.toString()}`}
                                        onClicked={(selected) =>
                                            onProductClicked(selected, index)
                                        }
                                        selected={selectedIndex === index}
                                        hasSelectableButton={true}
                                        style={{
                                            border: '1px solid #ced4da',
                                            marginTop: '10px'
                                        }}
                                        key={index}>
                                        <Product
                                            id={`product-${index.toString()}`}
                                            product={product}
                                        />
                                    </Selectable>
                                ))}
                            </FormInput>
                        </div>
                        <ModalDialog
                            showDialog={showDialog}
                            hideFooter={true}
                            onCloseClick={onCloseModalClick}>
                            <p>{t('modal_title')}</p>
                            <div className='joint-alloy-cosmetic-container'>
                                <button
                                    className='btn btn-primary'
                                    onClick={onJointAlloyConfirmation}>
                                    {t('alloy_wheel')}
                                </button>
                                <button
                                    className='btn btn-primary'
                                    onClick={onJointCosmeticConfirmation}>
                                    {t('cosmetic')}
                                </button>
                            </div>
                        </ModalDialog>
                    </div>
                    <div className='bottom-navigation'>
                        {navButton}
                        <ProgressButton
                            onClick={onPriorRepairRequestClicked}
                            navRoute={RoutePaths.ViewRequests}
                            locationState={{
                                product: products[selectedIndex]
                            }}
                            buttonStyle={requestButtonStyle}
                            text='Prior repair requests'
                        />
                        <ProgressButton
                            navRoute={RoutePaths.Home}
                            buttonStyle='btn btn-secondary progress-button'
                            text='Back'
                        />
                    </div>
                </>
            </LoadingContainer>
            <ReloadHandler reloadPath={RoutePaths.LandingPage} />
        </>
    );
};

export default ProductPage;
